/**
 * Integer discipline helpers.
 *
 * PARITY RULES — read before touching any file under `src/engine` or `src/art`:
 *
 * 1. Never use JavaScript bitwise operators (`>>`, `<<`, `&`, `|`, `^`) on a
 *    value that can exceed 2^31 - 1. They coerce to *int32* and will silently
 *    disagree with Solidity. Use `shr()` / `shl()` / `Math.floor(x / 2**n)`.
 * 2. 32-bit wrapping multiplication must go through `Math.imul` (exact) — never
 *    `a * b >>> 0` (loses precision above 2^53).
 * 3. Right shift on a possibly-negative value must floor, matching the EVM's
 *    `SAR`. `shr()` does this; `Math.trunc(x / 2**n)` does NOT.
 * 4. Division must truncate toward zero to match Solidity's `/`. Use `idiv()`.
 */

/** Mask for 32-bit values. */
export const MASK32 = 0xffffffff;

/**
 * `hash32`'s five multipliers, exported because three renderers use them.
 *
 * The TypeScript below, the payload's hand-written `h32` in `emit/html.ts`, and the GLSL in
 * `emit/glsl.ts` all have to compute the same avalanche — and the third one is a *string*, so
 * a compiler cannot check it. `emit/glsl.ts` interpolates these; `emit/html.ts` still spells
 * them out, and `tools/playercheck.ts` runs the shipped payload against this function to
 * prove that copy has not drifted. The Solidity mirror will quote them from here too.
 */
export const HASH_X_MUL = 0x27220a95;
export const HASH_Y_MUL = 0x85ebca6b;
export const HASH_SEED_MUL = 0x9e3779b1;
export const HASH_MIX_A = 0x2c1b3c6d;
export const HASH_MIX_B = 0x297a2d39;

/**
 * Collapse IEEE-754 negative zero to positive zero.
 *
 * `Math.trunc(-1 / 2)` and `(-256 % 256)` both produce `-0`, which compares
 * equal to `0` but is a *different* value under `Object.is`, `Map`/`Set` keys and
 * snapshot comparison. Solidity has no such value, so every integer helper
 * normalises before returning; otherwise a `-0` could silently take a different
 * branch in a memoisation table than the on-chain code does.
 */
function nz(x: number): number {
  return x === 0 ? 0 : x;
}

/** Arithmetic right shift that floors, matching EVM `SAR` for any magnitude. */
export function shr(x: number, bits: number): number {
  return nz(Math.floor(x / 2 ** bits));
}

/** Left shift, safe for any magnitude. */
export function shl(x: number, bits: number): number {
  return nz(x * 2 ** bits);
}

/** Integer division truncating toward zero, matching Solidity `/`. */
export function idiv(a: number, b: number): number {
  return nz(Math.trunc(a / b));
}

/** Euclidean-style modulo that always returns a non-negative result. */
export function umod(a: number, m: number): number {
  const r = a % m;
  return nz(r < 0 ? r + m : r);
}

/** Clamp to an inclusive integer range. */
export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

/** Absolute value. */
export function iabs(x: number): number {
  return nz(x < 0 ? -x : x);
}

/**
 * 32-bit integer avalanche hash. Wrapping arithmetic only, so the result is
 * identical to the Solidity implementation which masks with `& 0xffffffff`.
 *
 * `Math.imul` returns a *signed* int32; because signed and unsigned int32 share
 * the same residue class mod 2^32, summing them and applying `>>> 0` yields the
 * exact same value Solidity computes. The intermediate sum stays under 2^34, far
 * below 2^53, so no precision is lost.
 */
export function hash32(x: number, y: number, seed: number): number {
  let h =
    (Math.imul(x | 0, HASH_X_MUL) +
      Math.imul(y | 0, HASH_Y_MUL) +
      Math.imul(seed | 0, HASH_SEED_MUL)) >>>
    0;
  h ^= h >>> 15;
  h = Math.imul(h, HASH_MIX_A) >>> 0;
  h ^= h >>> 12;
  h = Math.imul(h, HASH_MIX_B) >>> 0;
  h ^= h >>> 15;
  return h >>> 0;
}

/** Development guard: assert a value stayed inside JS's exact-integer range. */
export function assertExact(x: number, where: string): number {
  if (!Number.isSafeInteger(x)) {
    throw new Error(`[parity] ${where}: value ${x} is not a safe integer`);
  }
  return x;
}
